import { Card, Stack, Title, Text, Badge } from '@repletoryreach/sdk-ui';
import type { templates } from '@repletoryreach/sdk-api';
import { CopyableId } from '../../../../components/CopyableId';
import { formatDateTime } from '../../../../lib/format';
import { useT } from '../../../../i18n/I18nProvider';

type Template = NonNullable<Awaited<ReturnType<typeof templates.get>>['data']>;

const MUTED = 'var(--rr-color-text-muted)';

/**
 * The tab is called Meta sync, and until now it held four of our own
 * timestamps and no word from Meta at all. `meta_status` is Meta's own review
 * verdict, `dev_fallback` says a non-production shortcut approved the template
 * instead, and `imported_at` is the only evidence a sync ever ran: without
 * those three the tab audits our journey rather than Meta's.
 */
export function MetaTrail({ template }: { template: Template }) {
  const t = useT();
  const dash = <Text role="bodySmall" c={MUTED}>{t('common.notAvailable')}</Text>;

  return (
    <Stack gap="lg">
      <Card withBorder>
        <Stack gap="md">
          <Stack gap="sm">
            <Title order={4}>{t('templates.view.meta.title')}</Title>
            <Text role="bodySmall" c={MUTED}>{t('templates.view.meta.hint')}</Text>
          </Stack>
          <Stack gap="sm">
            <Row
              label={t('templates.view.meta.metaStatus')}
              value={template.meta_status
                ? <Badge size="xs" variant="light" color="gray">{template.meta_status}</Badge>
                : dash}
            />
            <Row
              label={t('templates.view.meta.devFallback')}
              value={template.dev_fallback
                ? <Badge size="xs" variant="outline" color="orange">{t('templates.devApproved')}</Badge>
                : <Text role="bodySmall">{t('common.no')}</Text>}
            />
            <Row
              label={t('templates.view.meta.importedAt')}
              value={template.imported_at
                ? <Text role="bodySmall">{formatDateTime(template.imported_at)}</Text>
                : <Text role="bodySmall" c={MUTED}>{t('templates.view.meta.neverImported')}</Text>}
            />
            <Row
              label={t('templates.view.meta.templateId')}
              // The Graph API Explorer is the most likely next stop after this
              // tab, so the id is copyable in full rather than shown truncated.
              value={template.meta_template_id
                ? <CopyableId value={template.meta_template_id} label={t('templates.view.meta.copyTemplateId')} chars={20} />
                : dash}
            />
            <Row
              label={t('templates.view.meta.templateName')}
              value={template.meta_template_name
                ? <CopyableId value={template.meta_template_name} label={template.meta_template_name} chars={32} />
                : dash}
            />
            <Row
              label={t('templates.view.meta.submittedAt')}
              value={template.submitted_at ? <Text role="bodySmall">{formatDateTime(template.submitted_at)}</Text> : dash}
            />
            <Row
              label={t('templates.view.meta.approvedAt')}
              value={template.approved_at ? <Text role="bodySmall">{formatDateTime(template.approved_at)}</Text> : dash}
            />
            <Row
              label={t('templates.view.meta.rejectionReason')}
              value={template.rejection_reason ? <Text role="bodySmall">{template.rejection_reason}</Text> : dash}
            />
          </Stack>
        </Stack>
      </Card>
    </Stack>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 8, alignItems: 'baseline' }}>
      <Text role="caption" c={MUTED}>{label}</Text>
      <span>{value}</span>
    </div>
  );
}
